import { Container, Graphics, Sprite } from "pixi.js";
import { nextId } from "@shared/id";
import type { Game } from "@engine/core/Game";
import {
  AnimationStateMachine,
  type AnimClip,
  type Dir8,
} from "@engine/animation";
import type { Entity } from "./components";
import type { EcsWorld } from "./world";

export interface PlayerOpts {
  position: { x: number; y: number };
  color?: number;
  radius?: number;
  displayName?: string;
}

export const createLocalPlayer = (world: EcsWorld, game: Game, opts: PlayerOpts): Entity => {
  const radius = opts.radius ?? 11;
  const root = new Container({ label: "player" });
  const g = new Graphics();
  g.roundRect(-radius - 1, -radius - 1, (radius + 1) * 2, (radius + 1) * 2, 4)
    .fill(opts.color ?? 0x4cb1ff)
    .stroke({ color: 0x0b0d12, width: 2 });
  root.addChild(g);
  game.layers.attach("entity", root);
  root.position.set(opts.position.x, opts.position.y);

  return world.add({
    id: nextId("player"),
    kind: "player",
    position: { ...opts.position },
    velocity: { x: 0, y: 0 },
    heading: 0,
    sprite: { container: root, layer: "entity", radius },
    localControlled: true,
    displayName: opts.displayName ?? "Player",
  });
};

export interface NpcOpts {
  position: { x: number; y: number };
  homeRadius?: number;
  speed?: number;
  color?: number;
  radius?: number;
  displayName?: string;
}

export const createNpc = (world: EcsWorld, game: Game, opts: NpcOpts): Entity => {
  const radius = opts.radius ?? 9;
  const root = new Container({ label: "npc" });
  const g = new Graphics();
  g.circle(0, 0, radius).fill(opts.color ?? 0xffaa55).stroke({ color: 0x0b0d12, width: 1.5 });
  root.addChild(g);
  game.layers.attach("entity", root);
  root.position.set(opts.position.x, opts.position.y);

  const e: Entity = {
    id: nextId("npc"),
    kind: "npc",
    position: { ...opts.position },
    velocity: { x: 0, y: 0 },
    heading: 0,
    sprite: { container: root, layer: "entity", radius },
    wander: {
      home: { ...opts.position },
      radius: opts.homeRadius ?? 96,
      speed: opts.speed ?? 40,
      state: "idle",
      cooldown: 0,
      target: { ...opts.position },
    },
  };
  if (opts.displayName !== undefined) e.displayName = opts.displayName;
  return world.add(e);
};

export interface AudioEmitterOpts {
  position: { x: number; y: number };
  key: string;
  full: number;
  fade: number;
  cutoff: number;
  loop: boolean;
}

export const createAudioEmitter = (world: EcsWorld, opts: AudioEmitterOpts): Entity =>
  world.add({
    id: nextId("audio"),
    kind: "audio_emitter",
    position: { ...opts.position },
    audioEmitter: {
      key: opts.key,
      full: opts.full,
      fade: opts.fade,
      cutoff: opts.cutoff,
      loop: opts.loop,
    },
  });

// ----------------------------------------------------------------------------
// Animated archetypes (use TextureAtlasManager + AnimationStateMachine).
// Pass a per-state map of per-direction animation names that exist in the atlas.
// ----------------------------------------------------------------------------

/** Maps each animation state ("idle", "walk", "attack"…) to per-direction atlas
 *  animation names. Each atlas animation must already be registered in the
 *  loaded `Spritesheet`. */
export interface AnimSpec {
  state: string;
  /** Atlas animation key per direction (omit dirs you don't have). */
  byDir: Partial<Record<Dir8, string>>;
  fps?: number;
  loop?: boolean;
  /** Auto-transition when a non-loop clip finishes. */
  next?: string;
}

export interface AnimatedNpcOpts {
  position: { x: number; y: number };
  homeRadius?: number;
  speed?: number;
  radius?: number;
  displayName?: string;
  /** Atlas alias loaded via game.atlases.load(...). */
  atlas: string;
  /** All clips this entity will use. First clip is the initial state. */
  anims: AnimSpec[];
  /** Sprite tint (multiplied with texture). */
  tint?: number;
  /** Pivot offset Y so the feet sit on entity origin (default -2). */
  yPivotOffset?: number;
}

export const createAnimatedNpc = (world: EcsWorld, game: Game, opts: AnimatedNpcOpts): Entity => {
  const { container, sprite, sm } = buildAnimatedSprite(game, opts.atlas, opts.anims, {
    tint: opts.tint,
    yPivot: opts.yPivotOffset ?? -2,
  });
  game.layers.attach("entity", container);
  container.position.set(opts.position.x, opts.position.y);

  const radius = opts.radius ?? 9;
  const e: Entity = {
    id: nextId("npc"),
    kind: "npc",
    position: { ...opts.position },
    velocity: { x: 0, y: 0 },
    heading: Math.PI / 2, // facing down by default
    sprite: { container, layer: "entity", radius, lockRotation: true },
    animated: { sm, sprite, state: opts.anims[0]?.state ?? "idle" },
    wander: {
      home: { ...opts.position },
      radius: opts.homeRadius ?? 96,
      speed: opts.speed ?? 40,
      state: "idle",
      cooldown: 0,
      target: { ...opts.position },
    },
  };
  if (opts.displayName !== undefined) e.displayName = opts.displayName;
  return world.add(e);
};

export interface AnimatedPlayerOpts {
  position: { x: number; y: number };
  radius?: number;
  displayName?: string;
  atlas: string;
  anims: AnimSpec[];
  tint?: number;
  yPivotOffset?: number;
}

export const createAnimatedPlayer = (world: EcsWorld, game: Game, opts: AnimatedPlayerOpts): Entity => {
  const { container, sprite, sm } = buildAnimatedSprite(game, opts.atlas, opts.anims, {
    tint: opts.tint,
    yPivot: opts.yPivotOffset ?? -2,
  });
  game.layers.attach("entity", container);
  container.position.set(opts.position.x, opts.position.y);
  const radius = opts.radius ?? 11;
  return world.add({
    id: nextId("player"),
    kind: "player",
    position: { ...opts.position },
    velocity: { x: 0, y: 0 },
    heading: Math.PI / 2,
    sprite: { container, layer: "entity", radius, lockRotation: true },
    animated: { sm, sprite, state: opts.anims[0]?.state ?? "idle" },
    localControlled: true,
    displayName: opts.displayName ?? "Player",
  });
};

interface BuildOpts {
  tint?: number | undefined;
  yPivot: number;
}

function buildAnimatedSprite(game: Game, atlasAlias: string, anims: AnimSpec[], opts: BuildOpts): {
  container: Container;
  sprite: Sprite;
  sm: AnimationStateMachine;
} {
  if (anims.length === 0) throw new Error("createAnimated*: anims must contain at least one entry");
  const sprite = new Sprite();
  sprite.anchor.set(0.5, 0.5);
  sprite.y = opts.yPivot;
  if (opts.tint !== undefined) sprite.tint = opts.tint;

  // Build clip table for the AnimationStateMachine
  const clipTable: Record<string, AnimClip> = {};
  for (const a of anims) {
    const framesByDir: AnimClip["framesByDir"] = {};
    for (const dir of Object.keys(a.byDir) as Dir8[]) {
      const animName = a.byDir[dir];
      if (!animName) continue;
      framesByDir[dir] = game.atlases.getAnimation(atlasAlias, animName);
    }
    clipTable[a.state] = {
      framesByDir,
      ...(a.fps !== undefined ? { fps: a.fps } : {}),
      ...(a.loop !== undefined ? { loop: a.loop } : {}),
      ...(a.next !== undefined ? { next: a.next } : {}),
    };
  }

  const sm = new AnimationStateMachine(clipTable, { initial: anims[0]!.state });
  sm.attach(sprite);

  const container = new Container({ label: "animated" });
  container.addChild(sprite);
  return { container, sprite, sm };
}

/** Destroy an entity and any owned Pixi resources. */
export const destroyEntity = (world: EcsWorld, entity: Entity): void => {
  if (entity.sprite) {
    const c = entity.sprite.container;
    if (!c.destroyed) c.destroy({ children: true });
  }
  world.remove(entity);
};
